import {
  Inject,
  Injectable,
}                      from '@angular/core';
import { Store }       from '@ngrx/store';
import {
  combineLatest,
  map,
}                      from 'rxjs';
import {
  Beneficiary,
  canCreatePaymentFromAccount,
  DuplicatePaymentRequest,
  payDocumentType,
  PaymentData,
  RbDocument,
}                      from '~domain/types';
import { ENVIRONMENT } from '../app.constants';
import {
  addBeneficiary,
  addPayment,
  checkDuplicatePayments,
  clearDuplicatePayments,
  clearPaymentDocument,
  deleteBeneficiary,
  fetchClientBeneficiaries,
  fetchDocumentPaymentData,
  reloadPage,
  updateBeneficiary,
  updatePayment,
}                      from '../state/app.actions';
import {
  selectBeneficiaryEditing,
  selectClientBeneficiaries,
  selectClientBeneficiariesLoading,
  selectDuplicatePaymentsLatestDate,
  selectDuplicatePaymentsType,
  selectFetchingDocumentPaymentDataId,
  selectGlobalBeneficiaries,
  selectGlobalBeneficiariesLoading,
  selectOffice,
  selectPaymentAccounts,
  selectPaymentClients,
  selectPaymentDocumentType,
  selectPaymentDocumentUrl,
  selectPaymentSaving,
}                      from '../state/app.selectors';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  fetchingDocumentPaymentDataId$ = this.store.select(selectFetchingDocumentPaymentDataId);
  paymentAccounts$ = combineLatest([
    this.store.select(selectPaymentAccounts),
    this.store.select(selectOffice),
  ]).pipe(
    map(([paymentAccounts, office]) => office
      ? paymentAccounts.filter(account => canCreatePaymentFromAccount(account, office, this.environment.production))
      : []),
  );
  paymentClients$ = this.store.select(selectPaymentClients);
  paymentDocumentType$ = this.store.select(selectPaymentDocumentType);
  paymentDocumentUrl$ = this.store.select(selectPaymentDocumentUrl);
  globalBeneficiaries$ = this.store.select(selectGlobalBeneficiaries);
  clientBeneficiaries$ = this.store.select(selectClientBeneficiaries);
  globalBeneficiariesLoading$ = this.store.select(selectGlobalBeneficiariesLoading);
  clientBeneficiariesLoading$ = this.store.select(selectClientBeneficiariesLoading);
  paymentSaving$ = this.store.select(selectPaymentSaving);
  beneficiaryEditing$ = this.store.select(selectBeneficiaryEditing);
  duplicatePaymentsType$ = this.store.select(selectDuplicatePaymentsType);
  duplicatePaymentsLatestDate$ = this.store.select(selectDuplicatePaymentsLatestDate);

  constructor(
    @Inject(ENVIRONMENT)
    private readonly environment: Environment,
    private readonly store: Store,
  ) {
  }

  fetchNextDocumentPaymentData(document: RbDocument, queuedDocuments: RbDocument[], reloadAfterSave: boolean) {
    const documentId = document.id;
    const documentType = payDocumentType(document);
    if (documentType) {
      this.store.dispatch(fetchDocumentPaymentData({
        documentId,
        documentName: document.name,
        documentExtension: document.extension,
        documentType,
        clientId: document.client?.id,
        queuedDocuments,
        reloadAfterSave,
      }));
    }
  }

  savePayment(paymentData: PaymentData, anotherPayment: boolean, reload: boolean, queuedDocuments?: RbDocument[]) {
    if (!paymentData.id) {
      this.store.dispatch(addPayment({ paymentData, anotherPayment, queuedDocuments, reload, context: 'payment' }));
    } else {
      this.store.dispatch(updatePayment({ paymentData, reload }));
    }
  }

  fetchClientBeneficiaries(clientId: string) {
    this.store.dispatch(fetchClientBeneficiaries({ clientId }));
  }

  checkDuplicatePayments(clientId: string, request: DuplicatePaymentRequest) {
    this.store.dispatch(checkDuplicatePayments({ clientId, request }));
  }

  clearDuplicatePayments() {
    this.store.dispatch(clearDuplicatePayments());
  }

  updateBeneficiary(beneficiaryId: string, clientId: string, beneficiary: Beneficiary) {
    this.store.dispatch(updateBeneficiary({ beneficiaryId, clientId, beneficiary }));
  }

  addBeneficiary(clientId: string, beneficiary: Beneficiary) {
    this.store.dispatch(addBeneficiary({ clientId, beneficiary }));
  }

  deleteBeneficiary(beneficiaryId: string, clientId: string) {
    this.store.dispatch(deleteBeneficiary({ beneficiaryId, clientId }));
  }

  clearPaymentDocument() {
    this.store.dispatch(clearPaymentDocument());
  }

  reloadPage() {
    this.store.dispatch(reloadPage());
  }
}
